import { createContext, useEffect, useState } from 'react';
import { IMsalContext, useIsAuthenticated, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus, InteractionType } from '@azure/msal-browser';
import { loginRequest } from './authConfig';

/**
 * Props for the AgAuthMain component, defining the OIDC authentication flow configuration including an optional callback, loading/error UI, client ID, and app access state.
 */
interface AgOidcProps {
  LoadingComponent?: any;
  ErrorComponent: any;
  clientId: string;
  onAuthenticated?: (
    context: IMsalContext,
    setHasAppAccess: (value: ((prevState: object) => object) | object) => void,
    setLoadingProgress: (value: ((prevState: number) => number) | number) => void
  ) => Promise<void>;
  extraScopesToConsent?: string[];
  appAccesses: object;
  children: any;
}
// const isSafari = navigator.userAgent.includes('Safari');
// const interaction = isSafari ? InteractionType.Redirect : InteractionType.Silent;

/**
 * React context that holds the app access state computed after successful OIDC authentication.
 *
 * Created by React.createContext with an empty object literal as its default value. Consumers read appAccesses from this context.
 */
export const AgAuthContext = createContext({});
/**
 * The top-level auth wrapper that handles MSAL login, tracks authentication state, calls an optional onAuthenticated callback, and provides the appAccesses context to its children.
 *
 * Uses redirect-based authentication. Returns null while tokens are being set, rendering ErrorComponent if auth errors occur.
 */
export function AgAuthMain({
  LoadingComponent,
  ErrorComponent,
  children,
  onAuthenticated,
  extraScopesToConsent = [],
  appAccesses = {}
}: AgOidcProps) {
  const msalContext = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [onAuthenticatedCallbackRan, setOnAuthenticatedCallbackRan] = useState(false);
  const [loadProgress, setLoadingProgress] = useState(0);
  const [hasAppAccess, setHasAppAccess] = useState(appAccesses);
  const [didSetTokens, setDidSetTokens] = useState(false);
  const { error, login } = useMsalAuthentication(InteractionType.Redirect, {
    ...loginRequest,
    extraScopesToConsent: extraScopesToConsent
  });
  // useEffect(() => login(), []);
  useEffect(() => {
    if (
      msalContext &&
      isAuthenticated &&
      onAuthenticated &&
      msalContext.inProgress === InteractionStatus.None
    ) {
      if (!onAuthenticatedCallbackRan) {
        // console.log('run isAuthenticated');
        if (onAuthenticated)
          onAuthenticated(msalContext, setHasAppAccess, setLoadingProgress).then(() => {
            setDidSetTokens(true);
          });
        setOnAuthenticatedCallbackRan(true);
      }
    }
  }, [isAuthenticated, msalContext, onAuthenticatedCallbackRan]);
  useEffect(() => {
    if (isAuthenticated && !msalContext.instance.getActiveAccount() && msalContext.accounts[0]) {
      // console.log('setting active account')
      msalContext.instance.setActiveAccount(msalContext.accounts[0]);
    }
  }, [msalContext, isAuthenticated]);
  useEffect(() => {
    if (error instanceof InteractionRequiredAuthError) {
      login(InteractionType.Redirect, loginRequest).catch((er) => {
        console.log(er);
      });
    } else if (error) {
      console.error(error);
    }
  }, [error, login]);
  if (!didSetTokens) {
    return null;
    return <LoadingComponent loadProgress={loadProgress} />;
  }
  if (error && ErrorComponent) return ErrorComponent;
  return (
    <AgAuthContext.Provider
      value={{
        appAccesses: hasAppAccess
      }}
    >
      {children}
    </AgAuthContext.Provider>
  );
  // else if (error) return <ErrorComponent/>
  // else return <LoadingComponent/>
}
